import React, { useState, useEffect, useCallback, useContext } from "react";
import moment from "moment";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  Assessment,
  AttachMoney,
  Inventory2,
  ShoppingCart,
  EmojiEvents,
  Person,
  FilterAltOff,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ScheduleModal from "../../components/ScheduleModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const POS_VENDA_MSG =
  "Olá! Tudo certo com a sua compra? Qualquer coisa, estou à disposição 😊";

const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const EMPTY_FILTERS = {
  dateFrom: "",
  dateTo: "",
  productId: "",
  userId: "",
};

const MEDALS = ["#FFD700", "#C0C0C0", "#CD7F32"];

const SummaryCard = ({ icon, label, value, color }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: (t) => alpha(t.palette[color].main, 0.12),
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{value}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const RankingList = ({ title, icon, rows, emptyText }) => (
  <Paper sx={{ p: 2, height: "100%" }}>
    <Typography
      sx={{ fontWeight: 700, mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}
    >
      {icon} {title}
    </Typography>

    {rows.length === 0 ? (
      <Typography sx={{ color: "text.secondary", fontSize: 13, py: 2 }}>
        {emptyText}
      </Typography>
    ) : (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {rows.map((r, i) => (
          <Box
            key={r.productId ?? r.userId}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.text.primary, 0.03),
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: i < 3 ? "#000" : "text.secondary",
                bgcolor: i < 3 ? MEDALS[i] : (t) => alpha(t.palette.text.primary, 0.08),
                flexShrink: 0,
              }}
            >
              {i + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
                {r.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                {r.quantity} {r.quantity === 1 ? "item" : "itens"}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" }}>
              {brl(r.revenue)}
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Paper>
);

const RelatorioProdutos = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pageNumber, setPageNumber] = useState(1);

  const [summary, setSummary] = useState({
    totalRevenue: 0,
    totalQuantity: 0,
    salesCount: 0,
  });
  const [byProduct, setByProduct] = useState([]);
  const [byUser, setByUser] = useState([]);
  const [sales, setSales] = useState([]);
  const [agendando, setAgendando] = useState(null);
  const { user } = useContext(AuthContext);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([
          api.get("/products"),
          api.get("/users"),
        ]);
        setProducts(p.data || []);
        setUsers(u.data?.users || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageNumber };
      Object.keys(filters).forEach((k) => {
        if (filters[k]) params[k] = filters[k];
      });

      const { data } = await api.get("/products/report", { params });

      setSummary(data.summary);
      setByProduct(data.byProduct || []);
      setByUser(data.byUser || []);
      setHasMore(data.hasMore);
      // Pagina 1 substitui; demais concatenam ("carregar mais").
      setSales((prev) =>
        pageNumber === 1 ? data.sales || [] : [...prev, ...(data.sales || [])]
      );
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [filters, pageNumber]);

  useEffect(() => {
    const t = setTimeout(loadReport, 300);
    return () => clearTimeout(t);
  }, [loadReport]);

  const handleFilter = (field) => (e) => {
    setPageNumber(1);
    setFilters((f) => ({ ...f, [field]: e.target.value }));
  };

  const clearFilters = () => {
    setPageNumber(1);
    setFilters(EMPTY_FILTERS);
  };

  const hasFilters = Object.values(filters).some((v) => v);

  return (
    <MainContainer>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1, mb: 2 }}
        >
          <Assessment /> Relatório de Produtos
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="Data inicial"
                type="date"
                size="small"
                fullWidth
                value={filters.dateFrom}
                onChange={handleFilter("dateFrom")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                label="Data final"
                type="date"
                size="small"
                fullWidth
                value={filters.dateTo}
                onChange={handleFilter("dateTo")}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Produto"
                size="small"
                fullWidth
                value={filters.productId}
                onChange={handleFilter("productId")}
              >
                <MenuItem value="">Todos</MenuItem>
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                label="Vendedor"
                size="small"
                fullWidth
                value={filters.userId}
                onChange={handleFilter("userId")}
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                size="small"
                startIcon={<FilterAltOff />}
                onClick={clearFilters}
                disabled={!hasFilters}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Resumo */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              icon={<AttachMoney />}
              label="Valor vendido"
              value={brl(summary.totalRevenue)}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              icon={<Inventory2 />}
              label="Itens vendidos"
              value={summary.totalQuantity}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <SummaryCard
              icon={<ShoppingCart />}
              label="Nº de vendas"
              value={summary.salesCount}
              color="primary"
            />
          </Grid>
        </Grid>

        {/* Rankings */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <RankingList
              title="Produtos mais vendidos"
              icon={<EmojiEvents sx={{ fontSize: 18 }} />}
              rows={byProduct}
              emptyText="Nenhuma venda no período."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RankingList
              title="Vendedores"
              icon={<Person sx={{ fontSize: 18 }} />}
              rows={byUser}
              emptyText="Nenhuma venda no período."
            />
          </Grid>
        </Grid>

        {/* Detalhe */}
        <Paper sx={{ overflow: "hidden" }}>
          <Typography sx={{ fontWeight: 700, p: 2, pb: 1 }}>
            Vendas detalhadas
          </Typography>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Vendedor</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>Produto</TableCell>
                  <TableCell align="center">Qtd</TableCell>
                  <TableCell align="right">Valor unit.</TableCell>
                  <TableCell align="right">Montante</TableCell>
                  <TableCell align="center">Pós-venda</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      Nenhuma venda encontrada.
                    </TableCell>
                  </TableRow>
                )}

                {sales.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {s.soldAt ? moment(s.soldAt).format("DD/MM/YYYY HH:mm") : "—"}
                    </TableCell>
                    <TableCell>{s.user?.name || "—"}</TableCell>
                    <TableCell>{s.contact?.name || "—"}</TableCell>
                    <TableCell>{s.product?.name || "—"}</TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={s.quantity} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{brl(s.salePrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {brl(Number(s.salePrice) * Number(s.quantity))}
                    </TableCell>
                    <TableCell align="center">
                      {s.scheduleId ? (
                        <Chip size="small" color="success" label={s.schedule?.sendAt ? `Agendado ${moment(s.schedule.sendAt).format("DD/MM")}` : "Agendado"} />
                      ) : (
                        <Button size="small" color="warning" variant="outlined" sx={{ textTransform: "none" }} onClick={() => setAgendando(s)}>
                          Agendar pós-venda
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {hasMore && !loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <Button onClick={() => setPageNumber((p) => p + 1)}>
                Carregar mais
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
      {agendando && (
        <ScheduleModal
          open={!!agendando}
          onClose={() => setAgendando(null)}
          contactId={agendando.contactId || agendando.contact?.id}
          message={POS_VENDA_MSG}
          initialSendAt={moment().add(7, "days").format("YYYY-MM-DDTHH:mm")}
          onSaved={async (scheduleId) => {
            await api.put(`/product-sales/${agendando.id}/link-schedule`, { scheduleId });
            setSales((prev) => prev.map((x) => (x.id === agendando.id ? { ...x, scheduleId } : x)));
            setAgendando(null);
          }}
          user={user}
        />
      )}
    </MainContainer>
  );
};

export default RelatorioProdutos;
